import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5dc00b54"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "lg:h-screen flex flex-col lg:overflow-hidden" }

import { GridLayout, GridItem, Breakpoints, Breakpoint } from "grid-layout-plus";
import isMobile from "is-mobile";
import debounce from "lodash.debounce";
import { Component as VueComponent, onMounted, onUnmounted, Ref, ref, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import NavigationComponent from "@/components/layout/NavigationComponent.vue";
import { useCreateOrUpdateDashboard, useLastSelectedDashboardId } from "@/composables/dashboard";
import { useDebouncedBoundingClientRect } from "@/composables/screen";
import { useConfirmationModal } from "@/composables/toast";
import {
  Dashboard,
  DashboardGridSize,
  OculaiLayout,
  OculaiLayoutItem,
  OculaiResponsiveLayout,
  Widget,
} from "@/types/Dashboard";
import AddWidgetsSidebar from "@/views/dashboard/componentsV2/AddWidgetsSidebar.vue";
import BackgroundGrid from "@/views/dashboard/componentsV2/BackgroundGrid.vue";
import LoadingWidget from "@/views/dashboard/componentsV2/LoadingWidget.vue";
import PlannerWidget from "@/views/dashboard/componentsV2/PlannerWidget.vue";
import ProcessWidget from "@/views/dashboard/componentsV2/ProcessWidget.vue";
import ProjectOverviewV2Header from "@/views/dashboard/componentsV2/ProjectOverviewV2Header.vue";
import ReportWidget from "@/views/dashboard/componentsV2/ReportWidget.vue";
import StreamWidget from "@/views/dashboard/componentsV2/StreamWidget.vue";
import WeatherWidget from "@/views/dashboard/componentsV2/WeatherWidget.vue";
import {
  useHasPermissionToEditDashboard,
  useDashboardsWithDefault,
  useLayoutContext,
  useAspectRatioBugFix,
} from "@/views/dashboard/composables";
import {
  addWidgetsToLayout,
  calculateRealWidgetHeight,
  calculateRealWidgetWidth,
  convertDashboardToOculaiLayout,
  convertOculaiLayoutToDashboardWidgets,
  createDefaultLayout,
  createResponsiveLayout,
  desktopColumns,
  getAvailableWidgets,
  isMobileBreakpoint,
  liveStreamWidgetHeaderHeight,
  loadingWidgetsLayout,
  mobileColumns,
  removeWidgetFromLayout,
} from "@/views/dashboard/services/projectOverviewV2Layout";
import { useDragging } from "@/views/dashboard/services/widgetDragger";
import "./styles.css";

const addWidgetSidebarWidth = 300;
const gridMargin = 10;

export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectOverviewV2',
  setup(__props) {

const widgetComponents: Record<Widget, VueComponent> = {
  WeatherWidget,
  LoadingWidget,
  StreamWidget,
  PlannerWidget,
  ProcessWidget,
  ReportWidget,
};

const gridColumns: Breakpoints = {
  lg: desktopColumns,
  md: desktopColumns,
  sm: desktopColumns,
  xs: mobileColumns,
  xxs: mobileColumns,
};

const { t } = useI18n();

const containerRef = ref<HTMLDivElement | null>(null);
const gridLayout = ref<OculaiLayout>(loadingWidgetsLayout);
const gridLayoutComponent = ref<InstanceType<typeof GridLayout> | null>(null);
const responsiveLayouts: Ref<OculaiResponsiveLayout> = ref(
  createResponsiveLayout(loadingWidgetsLayout),
);
const breakpoint = ref<Breakpoint>("lg");
const ignoreNextLayoutUpdate = ref(false);
const createDashboardPromise = ref<Promise<Dashboard | null>>(Promise.resolve(null));
const isChanging = ref(false);
const isAddWidgetsSideBarOpen = ref(false);

const { layoutContext, isLoading: isLayoutContextLoading } = useLayoutContext();
const { dashboardsWithDefault, isLoading: areDashboardsLoading } =
  useDashboardsWithDefault(layoutContext);
const { createOrUpdateDashboard } = useCreateOrUpdateDashboard();
const { lastSelectedDashboardId, setLastSelectedDashboardId } = useLastSelectedDashboardId();
const streamWidgetColumns = ref(2);

// the wait time for debounce has to be 16, so it's in sync with the grid library,
// which does exactly the same debounce
const containerRect = useDebouncedBoundingClientRect(containerRef, 16);

const {
  handleWidgetDragStart,
  handleWidgetDrag,
  handleWidgetDragEnd,
  handleDragEnter,
  handleDragOver,
  draggedWidget,
} = useDragging(containerRect, gridLayoutComponent, gridLayout);

const showConfirmationModal = useConfirmationModal();

const hasPermissionToEditDashboard = useHasPermissionToEditDashboard();

const setLayout = (layout: OculaiLayout) => {
  gridLayout.value = layout;
  responsiveLayouts.value = createResponsiveLayout(layout);
};

const aspectRatioBugFix = useAspectRatioBugFix(gridLayout, setLayout);

const mobileBreakpoint = computed(() => (isAddWidgetsSideBarOpen.value ? 1 : 727));

const canEdit = computed(
  () =>
    !!currentDashboard.value &&
    !!layoutContext.value &&
    !isMobileBreakpoint(breakpoint.value) &&
    !isLoading.value &&
    !isMobile({ tablet: true, featureDetect: true }),
);

const canEditCurrentDashboard = computed(
  () =>
    canEdit.value && currentDashboard.value && hasPermissionToEditDashboard(currentDashboard.value),
);

const columnWidth = computed(() => {
  if (!containerRect.value) {
    return 30;
  }
  const numberOfColumns = gridColumns[breakpoint.value];
  // there are margins on the side too, that's why numberOfColumns + 1
  return (containerRect.value.width - (numberOfColumns + 1) * gridMargin) / numberOfColumns;
});

// see the formulas in https://www.notion.so/oculai/dcdd775d5e20463c893e98dfcf8713fe
const rowHeight = computed(
  () =>
    (4 * liveStreamWidgetHeaderHeight +
      3 * streamWidgetColumns.value * columnWidth.value -
      (streamWidgetColumns.value - 1) * gridMargin) /
    (4 * streamWidgetColumns.value),
);

const dashboardGridSize: Ref<DashboardGridSize> = computed(() => ({
  width: columnWidth.value,
  height: rowHeight.value,
  spacing: gridMargin,
}));

const calculateBestStreamWidgetColumns = () => {
  const widths = gridLayout.value
    .filter((widget) => widget.type === "StreamWidget")
    .map((widget) => widget.w);
  streamWidgetColumns.value = widths.length > 0 ? Math.max(...widths) : 2;
};

const handleBreakpointChanged = (newBreakpoint: Breakpoint) => {
  ignoreNextLayoutUpdate.value = true;
  breakpoint.value = newBreakpoint;
  // here one might think the second argument (layout) could be also used,
  // but it seems like that's not an up-to-date version from responsiveLayouts.value

  // also important, that we make a copy of the original array with [...]
  // so a layout updated event will be triggered and the ignoreNextLayoutUpdate toggled to false
  // this is needed, because when the grid is initialized, it might not trigger a layout updated event
  // after the breakpoint changed one. This would leave ignoreNextLayoutUpdate to stay true.
  gridLayout.value = [...responsiveLayouts.value[newBreakpoint]];
};

const currentDashboard = computed<Dashboard>(
  () =>
    dashboardsWithDefault.value.find(
      (dashboard) => dashboard._id === lastSelectedDashboardId.value,
    ) ||
    dashboardsWithDefault.value.find((dashboard) => dashboard.default) ||
    dashboardsWithDefault.value[0],
);

const version = ref(currentDashboard.value?.version || 0);

const isLoading = computed(() => areDashboardsLoading.value || isLayoutContextLoading.value);

const availableWidgets = computed(() =>
  isLoading.value || !layoutContext.value
    ? []
    : getAvailableWidgets(gridLayout.value, layoutContext.value),
);

const recalculateLayout = (options?: { triggerDbUpdate?: boolean }) => {
  const newLayout =
    isLoading.value || !currentDashboard.value || !layoutContext.value
      ? loadingWidgetsLayout
      : convertDashboardToOculaiLayout(currentDashboard.value, layoutContext.value);
  ignoreNextLayoutUpdate.value =
    options?.triggerDbUpdate !== undefined ? !options.triggerDbUpdate : true;
  setLayout(newLayout);
};

const calculateLayoutItemWidth = (layoutItem: OculaiLayoutItem) =>
  calculateRealWidgetWidth(dashboardGridSize.value, layoutItem.w);

const calculateLayoutItemHeight = (layoutItem: OculaiLayoutItem) =>
  calculateRealWidgetHeight(dashboardGridSize.value, layoutItem.h);

const doCreateOrUpdateDashboard = () => {
  version.value += 1;

  const dashboardToCreate: Dashboard = {
    ...currentDashboard.value,
    widgets: convertOculaiLayoutToDashboardWidgets(gridLayout.value),
    version: version.value,
  };

  // have to catch the error here, otherwise it's propagated as an unhandled error
  // it's already logged by vue-query
  const promise = createOrUpdateDashboard(dashboardToCreate).catch(() => null);
  if (dashboardToCreate.generated) {
    createDashboardPromise.value = promise;
  }
};

const debouncedCreateOrUpdateDashboard = debounce(doCreateOrUpdateDashboard, 500);

const createOrUpdateDashboardWithCreateWait = () =>
  createDashboardPromise.value.finally(() => {
    debouncedCreateOrUpdateDashboard();
  });

const handleLayoutUpdated = (layout: OculaiLayout) => {
  // this is needed for the case when the widget is grabbed, but in the end moved back to its original position
  // fortunately layout updated is triggered and isChanging can be toggled to false
  isChanging.value = false;

  // this we calculate always when layout changes
  calculateBestStreamWidgetColumns();

  if (ignoreNextLayoutUpdate.value) {
    ignoreNextLayoutUpdate.value = false;
    return;
  }
  if (isLoading.value || draggedWidget.value) {
    return;
  }
  setLayout(layout);
  createOrUpdateDashboardWithCreateWait();
};

const addWidget = (widget: OculaiLayoutItem) => {
  const newLayout = addWidgetsToLayout([widget], gridLayout.value);
  setLayout(newLayout);
};

const deleteWidget = (widget: OculaiLayoutItem) => {
  const newLayout = removeWidgetFromLayout(widget, gridLayout.value);
  setLayout(newLayout);
};

const handleResetDashboardClick = () => {
  showConfirmationModal({
    confirmAction: t("dashboard.reset_confirmation.confirm"),
    cancelAction: t("dashboard.reset_confirmation.cancel"),
    message: t("dashboard.reset_confirmation.message"),
    header: t("dashboard.reset_confirmation.header"),
  }).then((confirmed) => {
    if (confirmed && layoutContext.value) {
      const newLayout = createDefaultLayout(layoutContext.value);
      setLayout(newLayout);
    }
  });
};

const handleResize = (widgetId: string) => {
  isChanging.value = true;
  aspectRatioBugFix.handleResize(widgetId);
};

const handleResized = (widgetId: string) => {
  isChanging.value = false;
  aspectRatioBugFix.handleResized(widgetId);
};

watch([isLoading, layoutContext], (newValues, oldValues) => {
  const [newIsLoading, newLayoutContext] = newValues;
  const [oldIsLoading, oldLayoutContext] = oldValues;
  const triggerDbUpdate =
    !newIsLoading &&
    !oldIsLoading &&
    newLayoutContext &&
    oldLayoutContext &&
    newLayoutContext !== oldLayoutContext
      ? true
      : undefined;
  recalculateLayout({ triggerDbUpdate });
});

const isSameId = (newDashboard: Dashboard, oldDashboard: Dashboard) =>
  oldDashboard.generated || newDashboard._id === oldDashboard._id;

watch(currentDashboard, (newDashboard, oldDashboard) => {
  if (
    (!newDashboard && oldDashboard) ||
    (newDashboard && !oldDashboard) ||
    (newDashboard && oldDashboard && !isSameId(newDashboard, oldDashboard)) ||
    (newDashboard &&
      oldDashboard &&
      isSameId(newDashboard, oldDashboard) &&
      newDashboard.version > version.value)
  ) {
    version.value = newDashboard?.version || 0;
    isAddWidgetsSideBarOpen.value = isAddWidgetsSideBarOpen.value && canEditCurrentDashboard.value;
    recalculateLayout();
  }
});

onMounted(() => {
  recalculateLayout();
  document.body.classList.add("overflow-x-hidden");
});

onUnmounted(() => {
  document.body.classList.remove("overflow-x-hidden");
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(NavigationComponent, {
    activeTag: _ctx.$t('app_features.project_overview')
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(ProjectOverviewV2Header, {
          canEdit: canEdit.value,
          currentDashboard: currentDashboard.value,
          dashboards: _unref(dashboardsWithDefault),
          onToggleAddWidgetSidebar: _cache[0] || (_cache[0] = ($event: any) => (isAddWidgetsSideBarOpen.value = !isAddWidgetsSideBarOpen.value)),
          onSelectDashboard: _cache[1] || (_cache[1] = ($event: any) => (_unref(setLastSelectedDashboardId)($event._id))),
          onShowAddWidgetsSidebar: _cache[2] || (_cache[2] = ($event: any) => (isAddWidgetsSideBarOpen.value = $event)),
          onResetCurrentDashboard: handleResetDashboardClick
        }, null, 8, ["canEdit", "currentDashboard", "dashboards"]),
        _createElementVNode("div", {
          class: "flex-1 pb-1 lg:overflow-auto flex",
          style: _normalizeStyle({
          paddingLeft: '10px',
          paddingRight: isAddWidgetsSideBarOpen.value
            ? `${addWidgetSidebarWidth + gridMargin + 10}px`
            : _unref(isMobileBreakpoint)(breakpoint.value)
            ? '10px'
            : '0',
          scrollbarGutter: 'stable',
        })
        }, [
          _createElementVNode("div", {
            class: "select-none flex-1",
            ref_key: "containerRef",
            ref: containerRef,
            onDragenter: _cache[5] || (_cache[5] = 
//@ts-ignore
(...args) => (_unref(handleDragEnter) && _unref(handleDragEnter)(...args))),
            onDragover: _cache[6] || (_cache[6] = 
//@ts-ignore
(...args) => (_unref(handleDragOver) && _unref(handleDragOver)(...args)))
          }, [
            _createVNode(BackgroundGrid, {
              show: isChanging.value || !!_unref(draggedWidget),
              gridMargin: gridMargin,
              columnWidth: columnWidth.value,
              rowHeight: rowHeight.value
            }, {
              default: _withCtx(() => [
                (_openBlock(), _createBlock(_unref(GridLayout), {
                  ref_key: "gridLayoutComponent",
                  ref: gridLayoutComponent,
                  key: `${currentDashboard.value?._id}_${isLoading.value}`,
                  layout: gridLayout.value,
                  responsiveLayouts: responsiveLayouts.value,
                  rowHeight: rowHeight.value,
                  isDraggable: canEditCurrentDashboard.value,
                  isResizable: canEditCurrentDashboard.value,
                  useCssTransforms: "",
                  verticalCompact: "",
                  responsive: "",
                  cols: gridColumns,
                  margin: [gridMargin, gridMargin],
                  breakpoints: {
                lg: mobileBreakpoint.value,
                md: mobileBreakpoint.value,
                sm: mobileBreakpoint.value,
                xs: 0,
                xxs: 0,
              },
                  onLayoutUpdated: handleLayoutUpdated,
                  onBreakpointChanged: handleBreakpointChanged
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gridLayout.value, (item) => {
                      return (_openBlock(), _createBlock(_unref(GridItem), _mergeProps({
                        key: item.i,
                        onResize: handleResize,
                        onResized: handleResized,
                        onMove: _cache[3] || (_cache[3] = ($event: any) => (isChanging.value = true)),
                        onMoved: _cache[4] || (_cache[4] = ($event: any) => (isChanging.value = false)),
                        dragAllowFrom: ".vgl-item__drag-handle"
                      }, item), {
                        default: _withCtx(() => [
                          (_unref(draggedWidget)?.i !== item.i)
                            ? (_openBlock(), _createBlock(_resolveDynamicComponent(widgetComponents[item.type]), {
                                key: 0,
                                arg: item.arg,
                                width: calculateLayoutItemWidth(item),
                                height: calculateLayoutItemHeight(item),
                                dashboardGridSize: dashboardGridSize.value,
                                onDelete: ($event: any) => (deleteWidget(item))
                              }, null, 40, ["arg", "width", "height", "dashboardGridSize", "onDelete"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1040))
                    }), 128))
                  ]),
                  _: 1
                }, 8, ["layout", "responsiveLayouts", "rowHeight", "isDraggable", "isResizable", "margin", "breakpoints"]))
              ]),
              _: 1
            }, 8, ["show", "columnWidth", "rowHeight"])
          ], 544)
        ], 4)
      ]),
      (isAddWidgetsSideBarOpen.value && canEditCurrentDashboard.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed right-5 bottom-3 addWidgetsSideBarContainer",
            style: _normalizeStyle({ width: `${addWidgetSidebarWidth}px` })
          }, [
            _createVNode(AddWidgetsSidebar, {
              class: "h-full",
              widgets: availableWidgets.value,
              dashboardGridSize: dashboardGridSize.value,
              onAddWidget: _cache[7] || (_cache[7] = ($event: any) => (addWidget($event))),
              onClose: _cache[8] || (_cache[8] = ($event: any) => (isAddWidgetsSideBarOpen.value = false)),
              onWidgetDragStart: _unref(handleWidgetDragStart),
              onWidgetDrag: _unref(handleWidgetDrag),
              onWidgetDragEnd: _unref(handleWidgetDragEnd)
            }, null, 8, ["widgets", "dashboardGridSize", "onWidgetDragStart", "onWidgetDrag", "onWidgetDragEnd"])
          ], 4))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["activeTag"]))
}
}

})